<script setup lang="ts">
// [TODO] Use Teleport to move the modal to the end of the body
import { ref, computed, defineEmits, onMounted } from 'vue';
import { useEventListener } from '@vueuse/core';

interface DrawerProps {
  closeOnBackdropClick?: boolean;
  showCloseButton?: boolean;
  onClose?: () => void;
  direction?: 'left' | 'right' | 'top' | 'bottom';
}

const {
  closeOnBackdropClick = true,
  showCloseButton = true,
  onClose,
  direction = 'right',
} = defineProps<DrawerProps>();

const emit = defineEmits(['close']);
const show = defineModel('show', { type: Boolean, default: false });

const drawerClassName = computed(() => {
  const drawerClassNameMap = {
    left: 'drawer-left',
    right: 'drawer-right',
    top: 'drawer-top',
    bottom: 'drawer-bottom',
  };

  return `drawer-mask skip-context-menu ${drawerClassNameMap[direction] || ''}`;
});

const mousedDownOnBackdrop = ref(false);

const handleMouseDown = () => {
  mousedDownOnBackdrop.value = true;
};

const close = () => {
  show.value = false;
  emit('close');
  onClose?.();
};

const onMouseUp = () => {
  if (mousedDownOnBackdrop.value) {
    mousedDownOnBackdrop.value = false;
    if (closeOnBackdropClick) {
      close();
    }
  }
};

const onKeydown = (e: KeyboardEvent) => {
  if (show.value && e.code === 'Escape') {
    close();
    e.stopPropagation();
  }
};

useEventListener(document.body, 'mouseup', onMouseUp);
useEventListener(document, 'keydown', onKeydown);

onMounted(() => {
  if (onClose && typeof onClose === 'function') {
    // eslint-disable-next-line no-console
    console.warn(
      "[DEPRECATED] The 'onClose' prop is deprecated. Please use the 'close' event instead."
    );
  }
});
</script>

<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      :class="drawerClassName"
      transition="drawer"
      @mousedown="handleMouseDown"
    >
      <div
        class="relative max-h-full overflow-auto bg-n-alpha-3 shadow-md drawer-container rtl:text-right skip-context-menu"
        @mouse.stop
        @mousedown="event => event.stopPropagation()"
      >
        <woot-button
          v-if="showCloseButton"
          color-scheme="secondary"
          icon="dismiss"
          variant="clear"
          class="absolute z-10 ltr:right-2 rtl:left-2 top-2"
          @click="close"
        />
        <slot />
      </div>
    </div>
  </transition>
</template>

<style lang="scss">
.drawer-mask {
  @apply flex items-center justify-center bg-n-alpha-black2 backdrop-blur-[4px] z-[9990] h-full left-0 fixed top-0 w-full;

  .drawer-container {
    position: fixed;
    right: 0;
    top: 0;

    form,
    .modal-content {
      @apply pt-4 pb-8 px-8 self-center;

      a {
        @apply p-4;
      }
    }
  }
}

.drawer-right,
.drawer-left {
  .drawer-container {
    top: 0;
    width: 750px;
    height: 100vh;
  }
}

.drawer-top,
.drawer-bottom {
  .drawer-container {
    left: 0;
    width: 100vw;
    height: 450px;
  }
}

.drawer-right {
  .drawer-container {
    right: 0;
  }
}

.drawer-left {
  .drawer-container {
    left: 0;
  }
}

.drawer-top {
  .drawer-container {
    top: 0;
  }
}

.drawer-bottom {
  .drawer-container {
    bottom: 0;
  }
}
</style>
